import type { Item } from '../hp/types';
import type { LocalFootprint } from './footprint';

/**
 * Where loaded models stand on their items: the bounds a model's definition places it in, at its catalog size, in metres
 * round the item's position, as the model library measures them when a model arrives (`ModelLibrary.resolve`). A model's
 * position is its pivot, and the pivot need not be its middle: the straight flight stairs/3033 stands wholly to one side
 * of it, the round one stairs.gold/3034 most of a metre off. What has to follow the drawn model before the scene is built
 * or without it — the stairwell over a flight, on every floor — reads the footprint here rather than off the catalog box.
 */
export interface ModelBounds {
  /** The catalog size the library fits to the item's size (`fitScale`). */
  size: readonly [number, number, number];
  /** The model's bounds as its definition places and turns it, at the catalog size (metres, round its pivot, the item's axes). */
  min: readonly [number, number, number];
  max: readonly [number, number, number];
}

const known = new Map<string, ModelBounds>();
const listeners = new Set<(id: string) => void>();
let version = 0;

const same = (a: ModelBounds, b: ModelBounds) => [0, 1, 2].every(i => a.size[i] === b.size[i] && a.min[i] === b.min[i] && a.max[i] === b.max[i]);

/** A model has been measured; what reads footprints hears of it once something changed. */
export function noteModelBounds(id: string, bounds: ModelBounds): void {
  const was = known.get(id);
  if (!id || (was && same(was, bounds))) return;
  known.set(id, { size: [...bounds.size], min: [...bounds.min], max: [...bounds.max] });
  version++;
  for (const listener of listeners) listener(id);
}

/** Grows with every model measured, so a cache of footprints can tell it is out of date. */
export function modelBoundsVersion(): number { return version; }

/** `listener` hears the id of every model measured anew. */
export function subscribeModelBounds(listener: (id: string) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * How the model library scales a model to its item (`ModelLibrary.instance`): along the item's own axes, once the model
 * stands as its definition turns it — the item's size over the catalog size, x negative when the item is mirrored — so a
 * model its definition turns a quarter widens, deepens and mirrors as the item does. The app turns the item's scale into
 * the model's frame to the same end (DraggableFurnitureController.DomainUpdate). `scale` is the library's own, an older
 * bundle's raw mesh to catalog metres.
 */
export function fitScale(model: { size: readonly [number, number, number]; scale?: number }, item: Pick<Item, 'size' | 'mirrored'>): [number, number, number] {
  const axis = (want: number, have: number) => have > 1e-6 ? want / have : 1, scale = model.scale ?? 1;
  const sx = scale * axis(item.size.w, model.size[0]);
  return [item.mirrored ? -sx : sx, scale * axis(item.size.h, model.size[1]), scale * axis(item.size.d, model.size[2])];
}

/**
 * The item's footprint round its position, in its own turned frame, as its model is drawn: the measured bounds sized to
 * the item and mirrored (`fitScale`). Undefined until the model has been measured.
 */
export function measuredFootprint(item: Item): LocalFootprint | undefined {
  const model = known.get(item.modelRef.id);
  if (!model) return undefined;
  const [sx, , sz] = fitScale(model, item), x = [model.min[0] * sx, model.max[0] * sx], z = [model.min[2] * sz, model.max[2] * sz];
  const footprint = { minX: Math.min(...x), maxX: Math.max(...x), minZ: Math.min(...z), maxZ: Math.max(...z) };
  return footprint.maxX - footprint.minX > 1e-6 && footprint.maxZ - footprint.minZ > 1e-6 ? footprint : undefined;
}

/** For tests: forget every model measured. */
export function clearModelBounds(): void {
  if (!known.size) return;
  const ids = [...known.keys()];
  known.clear();
  version++;
  for (const id of ids) for (const listener of listeners) listener(id);
}
